import { Controller } from "@hotwired/stimulus"

// JWT Authentication Controller
//
// Handles persistent authentication for PWA using JWT tokens stored in localStorage.
// This is particularly important for iOS PWA where notification clicks may open
// the app in a new context without session cookies.
//
// Security considerations:
// - Tokens are stored in localStorage (persists across browser restarts)
// - Tokens include expiration and are validated server-side on each restore
// - Sessions can be invalidated server-side by destroying the Session record
// - Logout clears the stored token
//
// Usage:
// Add to the application layout:
// <div data-controller="jwt-auth"
//      data-jwt-auth-token-value="<%= flash[:jwt_token] %>"
//      data-jwt-auth-clear-token-value="<%= flash[:clear_jwt_token] %>"
//      data-jwt-auth-restore-url-value="<%= resotre_from_jwt_sessions_path %>">
// </div>

export default class extends Controller {
  static values = {
    token: String,
    restoreUrl: String,
    clearToken: Boolean,
    authenticated: Boolean,
    jwtRestorePage: Boolean
  }

  static JWT_STORAGE_KEY = "frankly_jwt_token"

  connect() {
    if (this.clearTokenValue) {
      this.clearToken()
      return
    }

    // Attempt JWT session restoration before any other logic.
    // This must run first to avoid clearing the token we need.
    if (this.shouldAttemptRestore()) {
      this.restoreSession()
      return
    }

    // On the dedicated restore page with no stored token, redirect to login
    if (this.jwtRestorePageValue && !this.getStoredToken()) {
      this.redirectToLogin()
      return
    }

    if (this.tokenValue) {
      this.storeToken(this.tokenValue)
    } else if (this.getStoredToken()) {
      if (!this.hasAuthenticatedSession() && this.isLoginPage()) {
        this.restoreSession()
      } else if (this.hasAuthenticatedSession()) {
        this.syncTokenToServiceWorker(this.getStoredToken())
      }
    }
  }

  hasAuthenticatedSession() {
    return this.authenticatedValue
  }

  isLoginPage() {
    return window.location.pathname === "/sessions/new"
  }

  // Store JWT token in localStorage and sync to service worker
  storeToken(token) {
    try {
      localStorage.setItem(this.constructor.JWT_STORAGE_KEY, token)
      this.syncTokenToServiceWorker(token)
    } catch (e) {
      console.warn("Failed to store JWT token:", e)
    }
  }

  // Retrieve stored JWT token
  getStoredToken() {
    try {
      return localStorage.getItem(this.constructor.JWT_STORAGE_KEY)
    } catch (e) {
      console.warn("Failed to retrieve JWT token:", e)
      return null
    }
  }

  // Clear stored JWT token (call on logout)
  clearToken() {
    try {
      localStorage.removeItem(this.constructor.JWT_STORAGE_KEY)
      this.clearIndexedDb()
      this.clearTokenFromServiceWorker()
    } catch (e) {
      console.warn("Failed to clear JWT token:", e)
    }
  }

  // Clear JWT directly from IndexedDB (page has access too, not just the SW)
  clearIndexedDb() {
    try {
      const request = indexedDB.open("frankly_sw", 1)
      request.onupgradeneeded = (e) => {
        e.target.result.createObjectStore("keyval")
      }
      request.onsuccess = (e) => {
        const db = e.target.result
        const tx = db.transaction("keyval", "readwrite")
        tx.objectStore("keyval").delete("jwt_token")
      }
    } catch (e) {
      // Best effort
    }
  }

  // Check if we should attempt session restoration
  // This happens when:
  // 1. URL has jwt_restore=true parameter (from notification click)
  // 2. We have a stored token
  // 3. We're not already authenticated (no session cookie)
  shouldAttemptRestore() {
    const params = new URLSearchParams(window.location.search)
    const hasRestoreParam = params.get("jwt_restore") === "true"
    const hasStoredToken = this.getStoredToken() !== null

    // Only attempt restore if we have the param, a token, and no active session
    return hasRestoreParam && hasStoredToken && !this.authenticatedValue
  }

  // Attempt to restore session using stored JWT
  async restoreSession() {
    const token = this.getStoredToken()
    if (!token) {
      if (this.jwtRestorePageValue) this.redirectToLogin()
      return
    }

    try {
      const response = await fetch(this.restoreUrlValue || "/sessions/restore_from_jwt", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        },
        body: JSON.stringify({ token: token }),
        credentials: "include"
      })

      if (response.ok) {
        const data = await response.json()

        if (data.token) {
          this.storeToken(data.token)
        }

        // Navigate to redirect_url when provided (e.g. restoring from /sessions/new),
        // otherwise reload the current page without jwt_restore param.
        if (data.redirect_url) {
          window.location.replace(data.redirect_url)
        } else {
          this.cleanUrlAndReload()
        }
      } else {
        this.clearToken()
        if (this.jwtRestorePageValue) {
          this.redirectToLogin()
        } else {
          this.cleanUrl()
        }
      }
    } catch (e) {
      console.warn("Failed to restore session:", e)
      if (this.jwtRestorePageValue) {
        this.redirectToLogin()
      } else {
        this.cleanUrl()
      }
    }
  }

  // Get CSRF token from meta tag
  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  // Remove jwt_restore parameter from URL
  cleanUrl() {
    const url = new URL(window.location.href)
    url.searchParams.delete("jwt_restore")
    window.history.replaceState({}, document.title, url.pathname + url.search)
  }

  // Remove jwt_restore parameter and reload page
  cleanUrlAndReload() {
    const url = new URL(window.location.href)
    url.searchParams.delete("jwt_restore")
    window.location.replace(url.pathname + url.search)
  }

  // Redirect to login when JWT restore fails or no token is available
  redirectToLogin() {
    window.location.replace("/sessions/new")
  }

  // Public method to logout and clear token
  logout() {
    this.clearToken()
  }

  // Post JWT to service worker so it can authenticate notification click tracking.
  // Uses navigator.serviceWorker.ready to handle the case where the SW isn't
  // controlling the page yet (first load, fresh install, or post-update).
  async syncTokenToServiceWorker(token) {
    if (!navigator.serviceWorker) return

    try {
      const registration = await navigator.serviceWorker.ready
      const sw = registration.active
      if (sw) sw.postMessage({ type: "JWT_STORE", token })
    } catch (e) {
      // Best effort — SW not available
    }
  }

  // Tell service worker to clear its stored JWT
  async clearTokenFromServiceWorker() {
    if (!navigator.serviceWorker) return

    try {
      const registration = await navigator.serviceWorker.ready
      const sw = registration.active
      if (sw) sw.postMessage({ type: "JWT_CLEAR" })
    } catch (e) {
      // Best effort
    }
  }
}
